import React from 'react';

const Features: React.FC = () => {
  // Stats data
  const stats = [
    { number: '70%', label: 'Time Savings' },
    { number: '95%+', label: 'Data Accuracy' },
    { number: '60%', label: 'Faster Processing' },
  ];

  // Core features data
  const coreFeatures = [
    {
      icon: '🤖',
      title: 'AI-Powered Document Processing',
      description: 'Intelligent automation for document handling and data extraction',
      items: [
        'Automatic OCR for Hindi & English documents',
        'GPT-4o powered data extraction',
        'Smart validation against master data',
        'Multi-format support (PDF, images)',
        'Context-aware parsing',
      ],
    },
    {
      icon: '📋',
      title: 'General Diary Management',
      description: 'Complete digital GD system with advanced tracking',
      items: [
        'Digital GD entries with incident tracking',
        'Suspect & victim profiling',
        'Weapon documentation & tracking',
        'Risk level assessment',
        'Criminal history integration',
      ],
    },
    {
      icon: '⚖️',
      title: 'Legal Notice Processing',
      description: 'Automated generation and tracking of legal documents',
      items: [
        'CRPC 129 Notice generation',
        'BNSL 129 Notice management',
        'Bond Order creation',
        'Court proceeding tracking',
        'Template-based automation',
      ],
    },
    {
      icon: '👮',
      title: 'Goonda Act Workflow',
      description: '8-stage approval process for habitual offenders',
      items: [
        'SHO Report → CO Sign → SDM Sign',
        'SP Permission → GD Entry',
        'DM Notice → Hearing → Final Order',
        'Evidence & attachment management',
        'Stage progression tracking',
      ],
    },
    {
      icon: '📊',
      title: 'Analytics & Reporting',
      description: 'Real-time insights for data-driven decision making',
      items: [
        'Case Analysis Reports',
        'Incident Trends (daily/weekly/monthly)',
        'Officer Performance metrics',
        'Location Heatmaps',
        'Excel export functionality',
      ],
    },
    {
      icon: '🔔',
      title: 'Intelligent Notifications',
      description: 'Priority-based alerts for critical cases',
      items: [
        'Real-time case updates',
        'Risk-based priority alerts',
        'Scheduled daily notifications (8 AM)',
        'Multi-level notification system',
        'Unread tracking',
      ],
    },
    {
      icon: '🔐',
      title: 'Role-Based Access Control',
      description: 'Secure, granular permissions system',
      items: [
        '6 user roles (SUPERADMIN to SHO)',
        'Multi-station assignment',
        'User status management',
        'JWT authentication',
        'Complete audit trails',
      ],
    },
    {
      icon: '🔍',
      title: 'Advanced Search & Filtering',
      description: 'Find any case or document in seconds',
      items: [
        '25+ filter parameters',
        'Full-text search',
        'Multi-criteria filtering',
        'Saved search templates',
        'Quick filters',
      ],
    },
    {
      icon: '👤',
      title: 'Criminal Management',
      description: 'Comprehensive suspect and criminal tracking',
      items: [
        'Criminal history database',
        'Multi-FIR support',
        'Repeat offender identification',
        'Threat level assessment',
        'Criminal profiling',
      ],
    },
  ];

  // Technology stack data
  const techStack = {
    frontend: ['React 19', 'TypeScript', 'Vite 7.0', 'Tailwind CSS 4.1', 'shadcn/ui', 'Radix UI', 'Zustand', 'React Router 7', 'TanStack Table', 'Recharts'],
    backend: ['Node.js', 'Express.js', 'MS SQL Server', 'Prisma ORM', 'JWT Auth', 'Winston Logger', 'Swagger/OpenAPI'],
    aiMl: ['Google Document AI', 'OpenAI GPT-4o', 'Mistral-7B', 'OCR Engine'],
    devops: ['Docker', 'Nginx', 'Helmet.js', 'Rate Limiting', 'Audit Logging'],
  };

  // Benefits data
  const benefits = [
    {
      icon: '👮‍♂️',
      title: 'For Officers',
      items: [
        '70% time savings on paperwork',
        'Mobile access from anywhere',
        'Easy search - find anything in seconds',
        'Reduced errors with AI validation',
        'Clear workflows at every stage',
      ],
    },
    {
      icon: '📈',
      title: 'For Management',
      items: [
        'Real-time visibility across all cases',
        'Data-driven decision making',
        'Performance monitoring dashboards',
        'Complete accountability with audit trails',
        'Ensure legal compliance',
      ],
    },
    {
      icon: '🏛️',
      title: 'For Department',
      items: [
        'Scalable to multiple districts',
        'Enterprise-grade security',
        'API-ready for integrations',
        'Future-proof technology',
      ],
    },
  ];

  // Success metrics data
  const successMetrics = [
    { icon: '⚡', title: 'Processing Speed', value: '70%', description: 'Faster than manual entry' },
    { icon: '🎯', title: 'Data Accuracy', value: '95%+', description: 'Accuracy in AI extraction' },
    { icon: '⏱️', title: 'Time Saved', value: '10+ hrs', description: 'Per officer per week' },
    { icon: '🔍', title: 'Search Efficiency', value: '<10 sec', description: 'Find any case instantly' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 text-slate-200">
      {/* Hero Section */}
      <section className="py-16 px-8 text-center bg-gradient-to-br from-blue-800/10 to-blue-500/10">
        <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-blue-500 bg-clip-text text-transparent">
          Pabandi Portal
        </h1>
        <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-2">
          AI-Powered Police Case Management System for Uttar Pradesh Police Department
        </p>
        <p className="text-blue-400 text-lg">
          Transforming Law Enforcement Through Digital Innovation
        </p>

        <div className="flex justify-center gap-12 flex-wrap mt-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-slate-800 py-6 px-8 rounded-xl min-w-[200px] border border-blue-400/20"
            >
              <div className="text-4xl font-bold text-blue-400">{stat.number}</div>
              <div className="text-slate-400 text-sm mt-2">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-8">
        {/* Core Features Section */}
        <section id="features" className="py-16">
          <h2 className="text-4xl font-bold text-blue-400 text-center mb-4">Core Features</h2>
          <p className="text-center text-slate-400 mb-12 text-lg">
            Comprehensive tools for modern law enforcement
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {coreFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-slate-800 rounded-xl p-8 border border-blue-400/20 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-400/30 hover:border-blue-400 cursor-pointer"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-2xl font-semibold text-blue-400 mb-4">{feature.title}</h3>
                <p className="text-slate-400 mb-4">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.items.map((item, idx) => (
                    <li key={idx} className="pl-6 relative text-slate-200">
                      <span className="absolute left-0 text-green-500 font-bold">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Technology Stack Section */}
        <section id="technology" className="py-16">
          <h2 className="text-4xl font-bold text-blue-400 text-center mb-4">Technology Stack</h2>
          <p className="text-center text-slate-400 mb-12 text-lg">
            Built with modern, enterprise-grade technologies
          </p>

          <div className="space-y-8">
            <div>
              <h3 className="text-blue-400 text-xl font-semibold mb-4">Frontend</h3>
              <div className="flex flex-wrap gap-4 justify-center">
                {techStack.frontend.map((tech, index) => (
                  <span
                    key={index}
                    className="bg-slate-800 py-3 px-6 rounded-full border border-blue-400/30 font-medium transition-all duration-300 hover:bg-blue-800 hover:scale-105"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-blue-400 text-xl font-semibold mb-4">Backend</h3>
              <div className="flex flex-wrap gap-4 justify-center">
                {techStack.backend.map((tech, index) => (
                  <span
                    key={index}
                    className="bg-slate-800 py-3 px-6 rounded-full border border-blue-400/30 font-medium transition-all duration-300 hover:bg-blue-800 hover:scale-105"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-blue-400 text-xl font-semibold mb-4">AI/ML</h3>
              <div className="flex flex-wrap gap-4 justify-center">
                {techStack.aiMl.map((tech, index) => (
                  <span
                    key={index}
                    className="bg-slate-800 py-3 px-6 rounded-full border border-blue-400/30 font-medium transition-all duration-300 hover:bg-blue-800 hover:scale-105"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-blue-400 text-xl font-semibold mb-4">DevOps & Security</h3>
              <div className="flex flex-wrap gap-4 justify-center">
                {techStack.devops.map((tech, index) => (
                  <span
                    key={index}
                    className="bg-slate-800 py-3 px-6 rounded-full border border-blue-400/30 font-medium transition-all duration-300 hover:bg-blue-800 hover:scale-105"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section id="benefits" className="py-16">
          <h2 className="text-4xl font-bold text-blue-400 text-center mb-4">Benefits</h2>
          <p className="text-center text-slate-400 mb-12 text-lg">
            Transformative impact across all levels
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-slate-800 to-blue-800/20 p-8 rounded-xl border-l-4 border-blue-400"
              >
                <div className="text-3xl mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-semibold text-blue-400 mb-4">{benefit.title}</h3>
                <ul className="space-y-2">
                  {benefit.items.map((item, idx) => (
                    <li key={idx} className="pl-6 relative text-slate-200">
                      <span className="absolute left-0 text-green-500 font-bold">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Success Metrics Section */}
        <section className="py-16">
          <h2 className="text-4xl font-bold text-blue-400 text-center mb-4">Proven Success Metrics</h2>
          <p className="text-center text-slate-400 mb-12 text-lg">
            Real results from Chandauli district deployment
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {successMetrics.map((metric, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-slate-800 to-blue-800/20 p-8 rounded-xl border-l-4 border-blue-400"
              >
                <div className="text-3xl mb-4">{metric.icon}</div>
                <h3 className="text-xl font-semibold text-blue-400">{metric.title}</h3>
                <p className="text-4xl text-blue-400 my-4">{metric.value}</p>
                <p className="text-slate-400">{metric.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-blue-800 to-blue-500 py-16 px-8 rounded-xl text-center my-16">
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Department?</h2>
          <p className="text-xl mb-8">
            Join the digital revolution in law enforcement with Pabandi Portal
          </p>
          <a
            href="https://cdipabandi.uppolice.co.in"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-blue-800 py-4 px-8 rounded-lg font-bold text-lg transition-transform duration-300 hover:scale-105"
          >
            View Live Demo →
          </a>
          <p className="mt-4 text-sm">
            Currently deployed: Chandauli District, Uttar Pradesh
          </p>
        </section>
      </div>

      {/* Footer */}
      <footer className="bg-slate-800 py-8 text-center border-t border-blue-400/20">
        <p>&copy; 2026 Pabandi Portal - AI-Powered Police Case Management System</p>
        <p className="mt-2 text-slate-400">
          Built for Uttar Pradesh Police Department
        </p>
        <p className="mt-4">
          <a
            href="https://cdipabandi.uppolice.co.in"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:underline"
          >
            Production Site
          </a>
        </p>
      </footer>
    </div>
  );
};

export default Features;
